import { Alert, CircularProgress, Stack, useTheme } from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { Dash, PlotData } from "plotly.js";
import { useMemo } from "react";
import { sorted } from "src/helpers";
import { ReviewerTrackingDailyEntry } from "src/store/api/generatedApi";
import {
  aggregateDailySplits,
  ConsumptionSplit,
  splitFromEntry,
} from "./consumptionSplits";
import {
  buildConsumptionImpactLayout,
  CONSUMPTION_IMPACT_HOVER_TEMPLATE,
} from "./consumptionImpactPlot";
import { Location } from "./useLocationSelections";

type DailyConsumptionImpactChartProps = Readonly<{
  entries: ReviewerTrackingDailyEntry[];
  selectedLocations: readonly Location[];
  showTotal: boolean;
  isLoading: boolean;
}>;

const LOCATION_DASH_CYCLE: Dash[] = ["dash", "dot", "dashdot", "longdash"];

type SplitTraceArgs = Readonly<{
  dates: readonly string[];
  splits: readonly ConsumptionSplit[];
  missedLabel: string;
  overshotLabel: string;
  missedColor: string;
  overshotColor: string;
  dash?: Dash;
}>;

const buildSplitTracePair = ({
  dates,
  splits,
  missedLabel,
  overshotLabel,
  missedColor,
  overshotColor,
  dash,
}: SplitTraceArgs): Partial<PlotData>[] => [
  {
    x: [...dates],
    y: splits.map((split) => split.missed),
    customdata: splits.map((split) => [
      split.missedCreated,
      split.missedIntroduced,
      0,
    ]),
    type: "scatter" as const,
    mode: "lines+markers" as const,
    name: missedLabel,
    marker: { color: missedColor, size: 5 },
    line: { color: missedColor, width: 3, dash },
    yaxis: "y",
    hovertemplate: CONSUMPTION_IMPACT_HOVER_TEMPLATE,
  },
  {
    x: [...dates],
    y: splits.map((split) => split.overshot),
    customdata: splits.map((split) => [
      0,
      split.overshotRemoved,
      split.overshotDeleted,
    ]),
    type: "scatter" as const,
    mode: "lines+markers" as const,
    name: overshotLabel,
    marker: { color: overshotColor, size: 5 },
    line: { color: overshotColor, width: 3, dash },
    yaxis: "y2",
    hovertemplate: CONSUMPTION_IMPACT_HOVER_TEMPLATE,
  },
];

export const DailyConsumptionImpactChart = ({
  entries,
  selectedLocations,
  showTotal,
  isLoading,
}: DailyConsumptionImpactChartProps) => {
  const { t } = useTenantTranslation();
  const palette = useTheme().palette;
  const dataPalette = palette.data;

  const traces: Partial<PlotData>[] = useMemo(() => {
    const sortedSelectedLocations = sorted(
      [...selectedLocations],
      (location) => location.name
    );
    const onlyTotal = showTotal && sortedSelectedLocations.length === 0;

    const locationTraces = sortedSelectedLocations.flatMap(
      (location, locationIndex) => {
        const locationEntries = sorted(
          entries.filter((entry) => entry.location_name === location.name),
          (entry) => entry.footage_date
        );
        return buildSplitTracePair({
          dates: locationEntries.map((entry) => entry.footage_date),
          splits: locationEntries.map(splitFromEntry),
          missedLabel: `${location.name} — ${t("consumptionFalseNegatives")}`,
          overshotLabel: `${location.name} — ${t("consumptionFalsePositives")}`,
          missedColor: dataPalette.red,
          overshotColor: dataPalette.orange,
          dash: LOCATION_DASH_CYCLE[locationIndex % LOCATION_DASH_CYCLE.length],
        });
      }
    );

    if (!showTotal) {
      return locationTraces;
    }

    const { dates: totalDates, splits: totalSplits } =
      aggregateDailySplits(entries);
    const totalTraces = buildSplitTracePair({
      dates: totalDates,
      splits: totalSplits,
      missedLabel: onlyTotal
        ? t("consumptionFalseNegatives")
        : `${t("total")} — ${t("consumptionFalseNegatives")}`,
      overshotLabel: onlyTotal
        ? t("consumptionFalsePositives")
        : `${t("total")} — ${t("consumptionFalsePositives")}`,
      missedColor: dataPalette.red,
      overshotColor: dataPalette.orange,
    });

    return [...locationTraces, ...totalTraces];
  }, [entries, selectedLocations, showTotal, t, dataPalette]);

  if (isLoading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={24} />
      </Stack>
    );
  }

  if (traces.length === 0 || entries.length === 0) {
    return <Alert severity="info">{t("noDataAvailable")}</Alert>;
  }

  return (
    <Plot
      data={traces}
      layout={buildConsumptionImpactLayout({
        palette,
        falseNegativesLabel: t("cFN"),
        falsePositivesLabel: t("cFP"),
      })}
      config={{
        responsive: true,
        displayModeBar: false,
        displaylogo: false,
      }}
      style={{ width: "100%", height: "450px" }}
      useResizeHandler
    />
  );
};
